// The editor's one door to the edit-project routes (E1), on the generated client. Its timelines
// are mapped by wire.ts (the wire's seconds to the editor's own milliseconds and back), so no
// screen reads a wire field.
import { useInfiniteQuery, useQuery, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { useEffect } from "react";

import { isApiError } from "../../api/errors";
import {
  getEditProjectChanges,
  getEditRender,
  getGetEditProjectQueryKey,
  handEditRenderToServer,
  sentEditRenderFile,
  startEditRenderFile,
  getGetEditRenderQueryKey,
  getListEditProjectsQueryKey,
  listEditProjects,
  useCreateEditProject,
  useDeleteEditProject,
  useGetEditProject,
  useReplaceEditProject,
  useStartEditRender,
} from "../../api/generated/endpoints/editor/editor";
import type {
  EditAspect,
  EditProject,
  EditProjectChanges,
  EditProjectPage,
  EditProjectSummary,
  EditProjectUpdate,
  EditRender,
  EditRenderMaker,
  EditRenderPurpose,
  JobError,
  JobStatus,
} from "../../api/generated/model";
import {
  EditAspect as AspectSchema,
  editProjectCreateNameMax,
  listEditProjectsQueryLimitDefault,
} from "../../api/generated/zod";
import { refreshLibraryLists } from "../../api/library-lists";
import { freshUrls, soonest, soonestExpiry } from "../../api/signed-urls";
import { isSettled } from "../../platform/jobs/status";
import { useStreamStatus } from "../../platform/stream-status";
import type { Timeline } from "./timeline";
import { fromWireTimeline, toWireTimeline } from "./wire";

export { liveFrame } from "./engine/project";
export { sameContent } from "./wire";

export type AspectRatio = EditAspect;
export const ASPECT_RATIOS: readonly AspectRatio[] = AspectSchema.options;
export const PROJECT_NAME_MAX = editProjectCreateNameMax;

/** An aspect ratio as CSS's `aspect-ratio`. */
export function cssRatio(aspect: AspectRatio): string {
  return aspect.replace(":", " / ");
}

export interface EditorProject {
  id: string;
  brandId: string;
  name: string;
  aspectRatio: AspectRatio;
  /** What a save sends back: a save over a newer version is refused (409). */
  version: number;
  /** Who made it: they, an admin or the owner change it (D6); every member reads it. */
  createdBy: string;
  updatedAt: string;
  timeline: Timeline;
}

/** A render of a saved version: its job's status, and the MP4 once it succeeded. */
export interface EditorRender {
  jobId: string;
  /** Where its file is made: in this browser, which posts it, or on the server (spec fast-save). */
  madeIn: EditRenderMaker;
  status: JobStatus;
  /** How far the running render is, 0 to 1, once the Save worker says (#269). */
  progress: number | null;
  error: JobError | null;
  /** The MP4 in the brand's library, once saved there. */
  assetId: string | null;
  url: string | null;
}

function fromWire(project: EditProject): EditorProject {
  return {
    id: project.id,
    brandId: project.brand_id,
    name: project.name,
    aspectRatio: project.aspect,
    version: project.version,
    createdBy: project.created_by,
    updatedAt: project.updated_at,
    timeline: fromWireTimeline(project.timeline, project.aspect),
  };
}

function fromWireRender(render: EditRender): EditorRender {
  return {
    jobId: render.job_id,
    madeIn: render.made_in,
    status: render.status,
    progress: render.progress,
    error: render.error,
    assetId: render.asset_id,
    url: render.url,
  };
}

/** A project as the list shows it (spec editor/project-list-pages.md): its facts and its first
 * picture's poster, never its timeline. */
export interface ProjectSummary {
  id: string;
  name: string;
  aspectRatio: AspectRatio;
  clipCount: number;
  durationMs: number;
  /** Its first picture's small picture; null with none, or when that file is gone. */
  poster: string | null;
  /** That picture's video file while it has no poster yet: the card draws its first frame. */
  video: string | null;
  createdBy: string;
  updatedAt: string;
}

function fromWireSummary(summary: EditProjectSummary): ProjectSummary {
  return {
    id: summary.id,
    name: summary.name,
    aspectRatio: summary.aspect,
    clipCount: summary.clip_count,
    durationMs: Math.round(summary.duration_s * 1000),
    poster: summary.poster,
    video: summary.video,
    createdBy: summary.created_by,
    updatedAt: summary.updated_at,
  };
}

type ProjectPages = InfiniteData<EditProjectPage, string | undefined>;

function summariesOf(data: ProjectPages): ProjectSummary[] {
  return data.pages.flatMap((page) => page.items.map(fromWireSummary));
}

/** The pages' posters are read again before they expire (G5). */
const pageUrls = freshUrls<ProjectPages>((data) => soonestExpiry(data.pages.map((page) => page.urls_expire_at)));

/**
 * The brand's projects, newest change first, as summaries 24 a page (spec
 * editor/project-list-pages.md); the list asks for the next page as its end nears. A create, a
 * delete or a save reads the loaded pages again, by the list's key.
 */
export function useProjectPages(teamId: string, brandId: string) {
  return useInfiniteQuery({
    ...pageUrls,
    // "pages" keeps these pages apart from a plain read of the same route.
    queryKey: [...getListEditProjectsQueryKey(teamId, brandId), "pages"],
    queryFn: ({ pageParam, signal }) =>
      listEditProjects(
        teamId,
        brandId,
        { limit: listEditProjectsQueryLimitDefault, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    select: summariesOf,
  });
}

export function useProject(teamId: string, brandId: string, projectId: string) {
  return useGetEditProject(teamId, brandId, projectId, {
    // A draft is edited on top of this read: a refetch must not replace it under the user.
    query: { select: fromWire, refetchOnWindowFocus: false },
  });
}

export interface NewProject {
  name: string;
  aspectRatio: AspectRatio;
}

/** Deletes a project (spec projects.md decision 7); the list is read again after, and the
 * project's own read is dropped. */
export function useDeleteProject(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  return useDeleteEditProject({
    mutation: {
      onSuccess: (_, { projectId }) => {
        queryClient.removeQueries({ queryKey: getGetEditProjectQueryKey(teamId, brandId, projectId) });
        return queryClient.invalidateQueries({ queryKey: getListEditProjectsQueryKey(teamId, brandId) });
      },
    },
  });
}

/** Creates a project; the list is read again after. */
export function useCreateProject(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  const create = useCreateEditProject({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getListEditProjectsQueryKey(teamId, brandId) }),
    },
  });
  return {
    ...create,
    create: (project: NewProject) =>
      create.mutateAsync({
        teamId,
        brandId,
        data: { name: project.name, aspect: project.aspectRatio },
      }),
  };
}

/** A save: the whole project, with the version its draft was loaded at. */
export interface SaveRequest {
  name: string;
  aspectRatio: AspectRatio;
  version: number;
  timeline: Timeline;
}

/**
 * Saves the whole project. The server's answer becomes the project's read, and the list is read
 * again. A save needs no idempotency key: the version guards it (a resend after a lost answer is
 * a 409, and the editor then reads the project again).
 */
export function useSaveProject(teamId: string, brandId: string, projectId: string) {
  const queryClient = useQueryClient();
  const replace = useReplaceEditProject({
    mutation: {
      onSuccess: (saved) => {
        queryClient.setQueryData(getGetEditProjectQueryKey(teamId, brandId, projectId), saved);
        void queryClient.invalidateQueries({
          queryKey: getListEditProjectsQueryKey(teamId, brandId),
        });
      },
    },
  });
  return {
    ...replace,
    save: async (save: SaveRequest): Promise<EditorProject> => {
      const data: EditProjectUpdate = {
        name: save.name,
        aspect: save.aspectRatio,
        timeline: toWireTimeline(save.timeline),
        version: save.version,
      };
      return fromWire(await replace.mutateAsync({ teamId, brandId, projectId, data }));
    },
  };
}

/** Starts a render of a saved version; its idempotency key is for `{version}`. */
export function useStartRender(teamId: string, brandId: string, projectId: string) {
  const start = useStartEditRender();
  return {
    ...start,
    /**
     * Renders a saved version; `folderId` keeps the video in that folder (live's Save). `madeIn`
     * asks for its file to be made in this browser; the answer's says where it is made.
     * `purpose` says a Download started it, for the journey's `video_exported`.
     */
    start: async (
      version: number,
      key: string,
      folderId: string | null = null,
      madeIn: EditRenderMaker = "server",
      purpose: EditRenderPurpose = "save",
    ): Promise<EditorRender> =>
      fromWireRender(
        await start.mutateAsync({
          teamId,
          brandId,
          projectId,
          data: {
            version,
            ...(folderId === null ? {} : { folder_id: folderId }),
            ...(madeIn === "browser" ? { made_in: madeIn } : {}),
            ...(purpose === "download" ? { purpose } : {}),
          },
          headers: { "Idempotency-Key": key },
        }),
      ),
  };
}

/**
 * A render made in this browser (spec fast-save): the policy its file is posted with, the word
 * that it is posted, so the render takes it in at once, and its hand-over to the server, which
 * renders the same saved version. `keepalive` lets the hand-over outlive a closing tab.
 */
export function browserRenderRoutes(teamId: string, brandId: string, projectId: string) {
  return {
    file: (jobId: string, bytes: number, signal?: AbortSignal) =>
      startEditRenderFile(teamId, brandId, projectId, jobId, { bytes }, signal === undefined ? {} : { signal }),
    sent: async (jobId: string) => fromWireRender(await sentEditRenderFile(teamId, brandId, projectId, jobId)),
    toServer: async (jobId: string, key: string, keepalive = false) =>
      fromWireRender(
        await handEditRenderToServer(teamId, brandId, projectId, jobId, { "Idempotency-Key": key }, { keepalive }),
      ),
  };
}

/** Read again this often while the stream is down and the render can still change. */
const FALLBACK_REFETCH_MS = 15_000;

/** A render's MP4 URL is signed: it is read again before `urls_expire_at` (G5). */
const renderUrls = freshUrls<EditRender>((render) => render.urls_expire_at);

/**
 * One render, live: the team stream's `job.transitioned` frames refetch it (event-handlers.ts);
 * while the stream is not live and it can still change, it is read every 15 s; and it is read
 * again before its MP4's signed URL expires. When it succeeds, the brand's library lists are
 * read again once, since the MP4 is saved there.
 */
export function useRender(teamId: string, brandId: string, projectId: string, jobId: string) {
  const stream = useStreamStatus();
  const queryClient = useQueryClient();
  const render = useQuery({
    queryKey: getGetEditRenderQueryKey(teamId, brandId, projectId, jobId),
    queryFn: ({ signal }) => getEditRender(teamId, brandId, projectId, jobId, { signal }),
    select: fromWireRender,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      const changing = status === undefined || !isSettled(status);
      const fallback = changing && stream !== "live" ? FALLBACK_REFETCH_MS : false;
      return soonest(fallback, renderUrls.refetchInterval(query));
    },
    refetchOnWindowFocus: renderUrls.refetchOnWindowFocus,
  });
  const succeeded = render.data?.status === "succeeded";
  useEffect(() => {
    if (succeeded) void refreshLibraryLists(queryClient, teamId, brandId);
  }, [succeeded, queryClient, teamId, brandId]);
  return render;
}

/** A save or a render refused because the project was saved elsewhere since it was loaded. */
export function isStaleVersion(error: unknown): boolean {
  return isApiError(error, "VERSION_CONFLICT");
}

/**
 * What the saves after version `after` changed, oldest first (spec live-sync-web.md); `complete`
 * false, with none, when the log cannot give them all: read the project whole instead.
 */
export function readChanges(teamId: string, brandId: string, projectId: string, after: number): Promise<EditProjectChanges> {
  return getEditProjectChanges(teamId, brandId, projectId, { after });
}
